import { Link } from 'react-router-dom';
import TestimonialSection from '@/pages/home/components/TestimonialSection';
import { SITE_NAME, SITE_ORIGIN, useSEO } from '@/hooks/useSEO';

const POSITIONS = [
  {
    icon: 'ri-timer-flash-line',
    title: '信息密度优先',
    desc: '拒绝为了篇幅而注水。我们把每一条内容压到刚好够用的长度，让阅读时间花在信息上。',
  },
  {
    icon: 'ri-stack-line',
    title: '分层阅读结构',
    desc: '要点、摘要、正文三层结构，你可以只读一层，也可以一直读到最后，节奏由你决定。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '中立整理立场',
    desc: '只做公开信息的归纳与梳理，不制造对立，也不替读者下结论。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续滚动更新',
    desc: '热点是流动的，我们保持每天两次更新，让速览始终跟得上讨论的进度。',
  },
];

const PRINCIPLES = [
  '只使用可公开查证的信息作为整理基础，并标注来源。',
  '摘要不夸大、不省略关键背景，尽量还原事件的完整语境。',
  '不对个人做出超出公开信息的判断，涉及争议时并列呈现各方说法。',
  '发现内容有误会优先更正，并在条目内留下更新记录。',
  '不接收影响内容判断的商业合作，广告会明确标注。',
];

const PHASES = [
  {
    step: '01',
    title: '从"太长不看"开始',
    desc: '最初只是想解决自己的问题：热点那么多，但完整读完一篇的耐心越来越少。于是我们开始把内容拆成要点。',
  },
  {
    step: '02',
    title: '把速览做成标准',
    desc: '拆得多了，就形成了一套固定的结构。要点写什么、摘要留多长，都有了可以被检验的标准。',
  },
  {
    step: '03',
    title: '把标准交给你',
    desc: '现在，5看料网把这套结构开放给每一位读者，也希望它成为你对信息效率的一个参考。',
  },
];

const ABOUT_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      name: '关于我们 - 5看料网',
      url: `${SITE_ORIGIN}/about`,
      inLanguage: 'zh-CN',
      description: '5看料网的品牌介绍、网站定位、发展理念与内容原则。',
    },
    {
      '@type': 'Organization',
      name: SITE_NAME,
      url: `${SITE_ORIGIN}/`,
      email: 'hello@5kanliao.com',
      description: '专注娱乐与网络热点的速览型内容站点。',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_ORIGIN}/about` },
      ],
    },
  ],
};

export default function About() {
  useSEO({
    title: '关于我们 | 5看料网速览快看',
    description:
      '了解5看料网的品牌定位、发展理念与内容原则。我们以速览、碎片化阅读与精简摘要的方式，帮助读者用更短的时间掌握热点核心信息。',
    keywords: '5看料网介绍,关于5看料网,速览平台,内容原则,精简摘要',
    path: '/about',
    jsonLd: ABOUT_JSON_LD,
  });

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="w-full px-4 md:px-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-body text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
            我们相信，读完才是内容真正的终点
          </h1>
          <p className="mt-4 max-w-2xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            5看料网是一个以速览为核心的内容站点。我们不追求更长的篇幅，只在乎你在几分钟里究竟带走了什么。
          </p>
        </div>
      </section>

      <section className="w-full py-14 md:py-20">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="h-[260px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cream%20editorial%20illustration%20of%20a%20neat%20reading%20desk%20with%20stacked%20cards%20and%20rounded%20geometric%20shapes%2C%20terracotta%20and%20amber%20accents%2C%20flat%20minimal%20style%2C%20soft%20grain%20texture%2C%20calm%20focused%20mood&width=1200&height=800&seq=kanliao-about-brand&orientation=landscape"
                alt="以速览阅读为主题的暖色编辑风格插画"
                title="5看料网品牌介绍 - 速览式阅读"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.24em] text-secondary-600">
                <span className="h-px w-6 bg-secondary-400" aria-hidden="true"></span>
                Brand
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                关于5看料网
              </h2>
              <div className="mt-5 flex flex-col gap-4">
                <p className="font-body text-sm leading-[1.9] text-foreground-700 md:text-base">
                  5看料网诞生于一个很朴素的观察：热点越来越多，但真正能读完的人越来越少。信息不是不够，而是密度太低。
                </p>
                <p className="font-body text-sm leading-[1.9] text-foreground-700 md:text-base">
                  于是我们换了一个思路，先把每条内容压缩成速览要点和精简摘要，让读者在几十秒内建立完整的认知框架，再决定要不要继续深入。这样，阅读的主动权重新回到了读者手里。
                </p>
                <p className="font-body text-sm leading-[1.9] text-foreground-700 md:text-base">
                  今天，5看料网每天更新两次，覆盖影视动态、综艺看点、音乐现场、网络热点、品牌风向与艺人观察六大板块，所有内容都遵循同一套速览标准。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100 py-14 md:py-20">
        <div className="w-full px-4 md:px-8">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
          <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            我们不是资讯的搬运工，而是信息的压缩器。以下四点，是我们理解自己角色的方式。
          </p>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {POSITIONS.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <span className="inline-flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.24em] text-secondary-600">
                <span className="h-px w-6 bg-secondary-400" aria-hidden="true"></span>
                Philosophy
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">发展理念</h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                从一个人的阅读困扰，到一套可以被复用的内容结构，中间只隔着一件事：坚持把复杂的东西说简单。
              </p>
            </div>
            <div className="lg:col-span-8">
              <ol className="flex flex-col gap-6">
                {PHASES.map((phase) => (
                  <li key={phase.step} className="flex gap-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                    <span className="font-heading text-2xl font-black text-accent-500 md:text-3xl">
                      {phase.step}
                    </span>
                    <span>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">{phase.title}</h3>
                      <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{phase.desc}</p>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-secondary-950 py-14 md:py-20">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <span className="inline-flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.24em] text-secondary-300">
                <span className="h-px w-6 bg-secondary-400" aria-hidden="true"></span>
                Principles
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">内容原则</h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-background-300">
                这些原则看起来朴素，但它们决定了我们每天如何选择、如何取舍、如何落笔。
              </p>
            </div>
            <div className="lg:col-span-8">
              <ul className="flex flex-col divide-y divide-secondary-800 border-y border-secondary-800">
                {PRINCIPLES.map((item, index) => (
                  <li key={item} className="flex items-start gap-4 py-4">
                    <span className="font-heading text-sm font-bold text-accent-400">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="font-body text-sm leading-relaxed text-background-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <TestimonialSection />

      <section className="w-full border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="w-full px-4 md:px-8">
          <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:p-10">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                想聊聊内容，或者提供一条线索？
              </h2>
              <p className="mt-2 max-w-xl font-body text-sm leading-relaxed text-foreground-600">
                无论是内容异议、商务合作，还是你想让我们整理的选题方向，都可以通过联系页面告诉我们。邮箱：hello@5kanliao.com
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-chat-3-line text-base" aria-hidden="true"></i>
              联系我们
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}